import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Pagination,
  Paper,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { format } from 'date-fns';
import VisibilityIcon from '@mui/icons-material/Visibility';
import EditIcon from '@mui/icons-material/Edit';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import PendingIcon from '@mui/icons-material/Pending';
import EditNoteIcon from '@mui/icons-material/EditNote';
import ImageIcon from '@mui/icons-material/Image';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile';
import api from '../../services/api';
import { t } from '../../utils/textUtils';

const normalizeStatus = (status) => {
  if (status === 'submitted') return 'pending';
  return status || 'draft';
};

const getAttachmentIcon = (fileType) => {
  if (fileType?.startsWith('image/')) return <ImageIcon fontSize="small" />;
  if (fileType === 'application/pdf') return <PictureAsPdfIcon fontSize="small" />;
  return <InsertDriveFileIcon fontSize="small" />;
};

const getAttachmentUrl = (fileUrl) => {
  if (!fileUrl) return '';
  if (/^https?:\/\//i.test(fileUrl)) return fileUrl;
  const base = api.defaults.baseURL || '';
  const baseRoot = base.replace(/\/api\/?$/, '');
  if (fileUrl.startsWith('/')) return `${baseRoot}${fileUrl}`;
  return `${baseRoot}/uploads/${fileUrl.replace(/^\/+/, '')}`;
};

const ProgressSubmissionList = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [submissions, setSubmissions] = useState([]);
  const [statusFilter, setStatusFilter] = useState('all');
  const [page, setPage] = useState(1);
  const [detailDialog, setDetailDialog] = useState({
    open: false,
    loading: false,
    submission: null
  });

  const limit = 10;

  const fetchSubmissions = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await api.get('/progress-submissions?mine=true&limit=1000');
      const rows = response.data?.progressSubmissions || [];
      setSubmissions(rows);
    } catch (err) {
      console.error('Error fetching progress submissions:', err);
      setError(err.response?.data?.message || 'Gagal memuat daftar pengajuan. Silakan coba lagi.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchSubmissions();
  }, [fetchSubmissions]);

  const filteredSubmissions = useMemo(() => {
    // Ensure submissions is always an array before mapping
    const submissionsArray = Array.isArray(submissions) ? submissions : [];
    const normalized = submissionsArray.map((item) => ({
      ...item,
      normalizedStatus: normalizeStatus(item.status)
    }));
    if (statusFilter === 'all') return normalized;
    return normalized.filter((item) => item.normalizedStatus === statusFilter);
  }, [submissions, statusFilter]);

  const totalPages = Math.max(1, Math.ceil(filteredSubmissions.length / limit));
  const pageData = filteredSubmissions.slice((page - 1) * limit, page * limit);

  useEffect(() => {
    if (page > totalPages) {
      setPage(1);
    }
  }, [page, totalPages]);

  const handleViewSubmission = async (submission) => {
    try {
      setDetailDialog({ open: true, loading: true, submission: null });
      const response = await api.get(`/progress-submissions/${submission.id}`);
      setDetailDialog({ open: true, loading: false, submission: response.data });
    } catch (err) {
      console.error('Error fetching submission detail:', err);
      setError(err.response?.data?.message || 'Gagal memuat detail pengajuan.');
      setDetailDialog({ open: false, loading: false, submission: null });
    }
  };

  const handleCloseDetail = () => {
    setDetailDialog({ open: false, loading: false, submission: null });
  };

  const handleEditSubmission = (submission) => {
    navigate(`/progress/submissions/${submission.id}/edit`);
  };

  const formatSubmittedDate = (submission) => {
    const raw = submission.submittedAt || submission.createdAt;
    if (!raw) return 'Belum submit';
    try {
      return format(new Date(raw), 'dd MMM yyyy');
    } catch (err) {
      return '-';
    }
  };

  const formatDisplayDate = (value) => {
    if (!value) return '-';
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return '-';
    return format(date, 'dd MMM yyyy');
  };

  const formatProgressPercent = (submission) => {
    const volume = Number(submission.volume);
    const quantity = Number(submission.taskQuantity);
    if (!Number.isFinite(volume) || !Number.isFinite(quantity) || quantity <= 0) return '-';
    const percent = Math.min(100, (volume / quantity) * 100);
    return `${percent.toFixed(1)}%`;
  };

  const renderStatusChip = (status) => {
    const normalized = normalizeStatus(status);
    switch (normalized) {
      case 'approved':
        return (
          <Chip
            icon={<CheckCircleIcon />}
            label="APPROVED"
            color="success"
            size="small"
            sx={{ fontWeight: 'bold' }}
          />
        );
      case 'pending':
        return (
          <Chip
            icon={<PendingIcon />}
            label="SUBMITTED"
            color="warning"
            size="small"
            sx={{ fontWeight: 'bold' }}
          />
        );
      case 'rejected':
        return (
          <Chip
            icon={<EditNoteIcon />}
            label="REJECTED"
            color="error"
            size="small"
            sx={{ fontWeight: 'bold' }}
          />
        );
      case 'draft':
      default:
        return (
          <Chip
            icon={<EditNoteIcon />}
            label="DRAFT"
            color="info"
            size="small"
            sx={{ fontWeight: 'bold' }}
          />
        );
    }
  };

  return (
    <Box sx={{ py: 4 }}>
      <Typography variant="h4" sx={{ mb: 3, fontWeight: 'bold', color: '#3f51b5', textAlign: 'center' }}>
        {t('Daftar Pengajuan')}
      </Typography>

      <Paper sx={{ p: 3, mb: 3 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={4}>
            <FormControl fullWidth>
              <InputLabel>Status</InputLabel>
              <Select
                value={statusFilter}
                label="Status"
                onChange={(e) => {
                  setStatusFilter(e.target.value);
                  setPage(1);
                }}
              >
                <MenuItem value="all">Semua</MenuItem>
                <MenuItem value="draft">Draft</MenuItem>
                <MenuItem value="pending">Submitted</MenuItem>
                <MenuItem value="approved">Approved</MenuItem>
                <MenuItem value="rejected">Rejected</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={8} sx={{ textAlign: { xs: 'left', md: 'right' } }}>
            <Button variant="outlined" onClick={fetchSubmissions} disabled={loading}>
              Refresh
            </Button>
          </Grid>
        </Grid>
      </Paper>

      {loading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress />
        </Box>
      )}

      {error && !loading && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {!loading && !error && (
        <Paper>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Project</TableCell>
                  <TableCell>Unit</TableCell>
                  <TableCell>Task</TableCell>
                  <TableCell align="center">Progress %</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Tanggal Submit</TableCell>
                  <TableCell align="center">Aksi</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {pageData.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={7} align="center">
                      Tidak ada pengajuan ditemukan.
                    </TableCell>
                  </TableRow>
                )}
                {pageData.map((submission) => (
                  <TableRow key={submission.id}>
                    <TableCell>{submission.projectName || '-'}</TableCell>
                    <TableCell>{submission.modularUnitName || '-'}</TableCell>
                    <TableCell>{submission.taskName || '-'}</TableCell>
                    <TableCell align="center">{formatProgressPercent(submission)}</TableCell>
                    <TableCell>{renderStatusChip(submission.status)}</TableCell>
                    <TableCell>{formatSubmittedDate(submission)}</TableCell>
                    <TableCell align="center">
                      <IconButton onClick={() => handleViewSubmission(submission)}>
                        <VisibilityIcon fontSize="small" />
                      </IconButton>
                      <IconButton
                        onClick={() => handleEditSubmission(submission)}
                        disabled={!['draft', 'rejected'].includes(normalizeStatus(submission.status))}
                      >
                        <EditIcon fontSize="small" />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
            <Pagination
              count={totalPages}
              page={page}
              onChange={(event, value) => setPage(value)}
              color="primary"
            />
          </Box>
        </Paper>
      )}

      <Dialog open={detailDialog.open} onClose={handleCloseDetail} maxWidth="md" fullWidth>
        <DialogTitle>Detail Pengajuan</DialogTitle>
        <DialogContent dividers>
          {detailDialog.loading && (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress />
            </Box>
          )}
          {!detailDialog.loading && detailDialog.submission && (
            <>
              <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>
                {detailDialog.submission.projectName || '-'}
              </Typography>
              <Typography variant="body2" sx={{ mb: 2, color: 'text.secondary' }}>
                {detailDialog.submission.modularUnitName || '-'} • {detailDialog.submission.workCategoryName || '-'}
              </Typography>

              <Grid container spacing={2} sx={{ mb: 2 }}>
                <Grid item xs={12} md={6}>
                  <Typography variant="body2"><strong>Task:</strong> {detailDialog.submission.taskName || '-'}</Typography>
                  <Typography variant="body2"><strong>Volume:</strong> {detailDialog.submission.volume || 0} {detailDialog.submission.taskUnit || ''}</Typography>
                  <Typography variant="body2"><strong>Area:</strong> {detailDialog.submission.area || '-'}</Typography>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography variant="body2"><strong>Status:</strong> {renderStatusChip(detailDialog.submission.status)}</Typography>
                  <Typography variant="body2"><strong>Tanggal Submit:</strong> {formatSubmittedDate(detailDialog.submission)}</Typography>
                  <Typography variant="body2"><strong>Progress Work Date:</strong> {formatDisplayDate(detailDialog.submission.progressWorkDate)}</Typography>
                </Grid>
              </Grid>

              {detailDialog.submission.rejectionReason && (
                <Alert severity="error" sx={{ mb: 2 }}>
                  Alasan Reject: {detailDialog.submission.rejectionReason}
                </Alert>
              )}

              <Typography variant="body2" sx={{ mb: 2 }}>
                <strong>Catatan:</strong> {detailDialog.submission.notes || '-'}
              </Typography>

              <Divider sx={{ my: 2 }} />

              <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 'bold' }}>
                Lampiran
              </Typography>
              {detailDialog.submission.attachments?.length ? (
                <ul style={{ margin: 0, paddingLeft: 18 }}>
                  {detailDialog.submission.attachments.map((attachment) => (
                    <li key={attachment.id} style={{ marginBottom: 6 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        {getAttachmentIcon(attachment.fileType)}
                        <a
                          href={getAttachmentUrl(attachment.fileUrl)}
                          target="_blank"
                          rel="noreferrer"
                          style={{ color: '#1e88e5', textDecoration: 'none' }}
                        >
                          {attachment.fileName || 'Lampiran'}
                        </a>
                      </Box>
                    </li>
                  ))}
                </ul>
              ) : (
                <Typography variant="body2" color="text.secondary">
                  Tidak ada lampiran.
                </Typography>
              )}
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDetail}>Tutup</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ProgressSubmissionList;
